if (!customElements.get('media-gallery')) {
  const getInitialViewerScrollUtils = () => {
    const ScrollUtils = window.AuremeProductGallery?.ScrollUtils;
    const hasInitialViewerScrollUtils =
      typeof ScrollUtils?.isNearScrollLeft === 'function' &&
      typeof ScrollUtils?.scrollLeftWithoutAnimation === 'function' &&
      typeof ScrollUtils?.getSettledScrollLeftFrameCount === 'function';
    return hasInitialViewerScrollUtils ? ScrollUtils : null;
  };
  const FALLBACK_ACTIVE_MEDIA_OPTIONS = Object.freeze({
    thumbnailAlign: 'visible',
    thumbnailAnchorRatio: 0.4,
    allowViewportScroll: true,
    behavior: 'smooth',
    viewerScrollBehavior: 'auto',
    skipImmediateGallerySync: false,
    lockCounterUntilViewerScrollSettled: false,
    announce: true,
    playMedia: true,
    scrollThumbnail: true,
    scrollViewer: true,
  });

  /* 依存読込失敗時も public method 呼び出しで例外化しないよう、安全な fallback を使う */
  function createUnavailableActiveMediaSync() {
    return {
      getActiveMediaOptions(options = {}) {
        return {
          ...FALLBACK_ACTIVE_MEDIA_OPTIONS,
          ...options,
        };
      },
      setActiveMedia() {},
      syncFromSlideChanged() {},
      updateActiveMediaState() {
        return null;
      },
      syncActiveImageState() {},
      disconnect() {},
    };
  }

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="GalleryDots"]'),
        };
        this._mediaItemSelector = '[id^="Slide-"][data-media-id]';
        this._onSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this._onNavigationClick = this.onNavigationClick.bind(this);
        this._onMediaItemClick = this.onMediaItemClick.bind(this);
        this._onPreviousButtonClick = this.handleSingleVisibleSlideButtonClick.bind(this);
        this._onNextButtonClick = this.handleSingleVisibleSlideButtonClick.bind(this);
        this._enableSlideChangeAnnouncements = this.enableSlideChangeAnnouncements.bind(this);
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mqlDesktop = window.matchMedia('(min-width: 990px)');
        /*
         * ActiveMediaSync 側が読み取るフラグ。初回同期などプログラム的な切替では
         * スクリーンリーダーへの読み上げを誤発火させないため、ユーザー操作（タップ・クリック）由来の
         * 切替でのみ true にする。
         */
        this._allowSlideChangeAnnouncements = false;
        const ActiveMediaSync = window.AuremeMediaGallery?.ActiveMediaSync;
        if (typeof ActiveMediaSync !== 'function') {
          console.error('[media-gallery] active media sync is not loaded.');
        }
        this.activeMediaSync =
          typeof ActiveMediaSync === 'function'
            ? new ActiveMediaSync(this)
            : createUnavailableActiveMediaSync();
        /* ナビゲーション要素をキャッシュ（DOM変更時はrefreshNavigationItemsで再取得） */
        this._navigationItems = this._queryNavigationItems();
        this._uniqueTargetCount = new Set(
          this._navigationItems.map((item) => item.dataset.target),
        ).size;
        if (!this._navigationItems.length) return;

        this.elements.viewer.addEventListener('slideChanged', this._onSlideChanged);
        this.addEventListener('click', this._onNavigationClick);
        this.elements.viewer.addEventListener('click', this._onMediaItemClick);
        this.elements.viewer.addEventListener('touchstart', this._enableSlideChangeAnnouncements, {
          passive: true,
        });
        this.bindSingleVisibleSlideNavigation();
        this._initialActiveItem = this.getInitialActiveNavigationItem();
        if (this._initialActiveItem)
          this.updateCounter(this._initialActiveItem.dataset.mediaPosition);
        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        /* メイン画像の高さ変化を監視してサムネイル列の高さを同期 */
        if (this.elements.thumbnails && this.elements.viewer) {
          this._thumbnailResizeObserver = new ResizeObserver(this.syncThumbnailHeight.bind(this));
          this._thumbnailResizeObserver.observe(this.elements.viewer);
          requestAnimationFrame(() => this.syncThumbnailHeight());
        }

        /* 初回描画のズレを抑えるため、初期同期は即時実行し、必要な場合だけ次フレームで補完する */
        this.syncInitialActiveMedia();
        requestAnimationFrame(() => {
          if (!this.needsInitialSyncFollowup()) return;
          this.syncInitialActiveMedia();
        });
      }

      _queryNavigationItems() {
        return Array.from(this.querySelectorAll('[data-target]')).filter((item) =>
          item.querySelector('button'),
        );
      }

      getInitialActiveNavigationItem() {
        const activeNavigationItem = this.navigationItems.find((item) =>
          item.querySelector('button')?.hasAttribute('aria-current'),
        );
        if (activeNavigationItem) return activeNavigationItem;

        const activeViewerItem = this.elements.viewer?.querySelector(
          '.product__media-item.is-active',
        );
        if (!activeViewerItem?.dataset?.mediaId) return this.navigationItems[0];

        return (
          this.navigationItems.find(
            (item) => item.dataset.target === activeViewerItem.dataset.mediaId,
          ) || this.navigationItems[0]
        );
      }

      needsInitialSyncFollowup() {
        const initialActiveItem = this.getInitialActiveNavigationItem();
        if (!initialActiveItem?.dataset?.target) return false;

        const activeMedia = this.elements.viewer?.querySelector(
          `[data-media-id="${initialActiveItem.dataset.target}"]`,
        );
        if (!activeMedia) return false;

        if (!this.mqlDesktop.matches) {
          const ScrollUtils = getInitialViewerScrollUtils();
          if (!ScrollUtils) return false;
          const scrollContainer = activeMedia.parentElement;
          if (!scrollContainer) return false;
          return !ScrollUtils.isNearScrollLeft(scrollContainer, activeMedia.offsetLeft);
        }

        if (!this.elements.thumbnails) return false;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${initialActiveItem.dataset.target}"]`,
        );
        if (!activeThumbnail) return false;

        const scrollContainer = this.getThumbnailScrollContainer();
        if (!scrollContainer) return false;

        const metrics = this.getThumbnailScrollMetrics(activeThumbnail, scrollContainer);
        return (
          metrics.top < scrollContainer.scrollTop ||
          metrics.bottom > scrollContainer.scrollTop + scrollContainer.clientHeight
        );
      }

      syncInitialActiveMedia() {
        const initialActiveItem = this._initialActiveItem || this.getInitialActiveNavigationItem();
        this._initialActiveItem = null;
        if (!initialActiveItem?.dataset?.target) return;

        this._isInitialSyncing = true;
        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = '';
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }

        const activeMedia = this.activeMediaSync.updateActiveMediaState(
          initialActiveItem.dataset.target,
        );
        if (!activeMedia) return;

        this.activeMediaSync.syncActiveImageState(activeMedia);
        this.syncNavigationState(initialActiveItem.dataset.target, {
          behavior: 'auto',
          scrollThumbnail: this.mqlDesktop.matches,
          thumbnailAlign: 'start',
        });
        this.updateCounter(initialActiveItem.dataset.mediaPosition);
        this.syncThumbnailHeight();
        if (!this.syncInitialViewerPosition(activeMedia)) {
          this.finishInitialSync();
        }
      }

      /* 差分同期でナビゲーション要素が増減した後にキャッシュを更新する */
      refreshNavigationItems() {
        this._navigationItems = this._queryNavigationItems();
        this._uniqueTargetCount = new Set(
          this._navigationItems.map((item) => item.dataset.target),
        ).size;
      }

      get navigationItems() {
        return this._navigationItems || [];
      }

      bindSingleVisibleSlideNavigation() {
        const previousButton = this.elements.viewer?.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
        if (!previousButton || !nextButton) return;

        this._previousButton = previousButton;
        this._nextButton = nextButton;

        /* タブレット帯の単一表示モードでは、共通sliderのscroll計算では送れないため手動で切り替える */
        previousButton.addEventListener('click', this._onPreviousButtonClick, true);
        nextButton.addEventListener('click', this._onNextButtonClick, true);
      }

      enableSlideChangeAnnouncements() {
        this._allowSlideChangeAnnouncements = true;
      }

      isSingleVisibleSlideMode() {
        if (!this.mql.matches || this.mqlDesktop.matches) return false;

        const hiddenSlide = this.elements.viewer?.querySelector('[data-media-id]:not(.is-active)');
        if (!hiddenSlide) return false;

        return getComputedStyle(hiddenSlide).display === 'none';
      }

      handleSingleVisibleSlideButtonClick(event) {
        this.enableSlideChangeAnnouncements();
        if (!this.isSingleVisibleSlideMode()) return;

        const mediaItems = Array.from(
          this.elements.viewer.sliderItems ||
            this.elements.viewer.querySelectorAll(this._mediaItemSelector),
        );
        if (mediaItems.length < 2) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const fallbackIndex = activeIndex >= 0 ? activeIndex : 0;
        const targetIndex =
          event.currentTarget.name === 'next'
            ? (fallbackIndex + 1) % mediaItems.length
            : (fallbackIndex - 1 + mediaItems.length) % mediaItems.length;
        const targetMediaId = mediaItems[targetIndex]?.dataset?.mediaId;
        if (!targetMediaId) return;

        this.setActiveMedia(targetMediaId, {
          allowViewportScroll: false,
        });
      }

      getActiveMediaOptions(options = {}) {
        return this.activeMediaSync.getActiveMediaOptions(options);
      }

      setActiveMedia(mediaId, options = {}) {
        this.activeMediaSync.setActiveMedia(mediaId, options);
      }

      onNavigationClick(event) {
        const navigationButton = event.target.closest('[data-target] button');
        if (!navigationButton || !this.contains(navigationButton)) return;

        const navigationItem = navigationButton.closest('[data-target]');
        if (!navigationItem?.dataset?.target) return;

        event.preventDefault();
        /* ギャラリー閲覧はバリアント選択と独立させ、表示メディアだけを切り替える */
        this.setActiveMedia(navigationItem.dataset.target);
      }

      onMediaItemClick(event) {
        this.enableSlideChangeAnnouncements();
        if (this.mqlDesktop.matches) return;
        if (
          event.target.closest('[data-target] button') ||
          event.target.closest('.slider-button') ||
          event.target.closest('.product__media-toggle')
        ) {
          return;
        }

        const mediaItem = event.target.closest('[data-media-id]');
        const imageOpener = event.target.closest('.product__modal-opener--image');
        if (!mediaItem || !imageOpener || !this.elements.viewer.contains(mediaItem)) return;

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(this._mediaItemSelector),
        );
        if (mediaItems.length < 2) return;

        event.preventDefault();
        const currentMediaId = mediaItem.dataset.mediaId;
        if (!currentMediaId) return;

        const gallerySlider = this.elements.viewer;
        if (
          gallerySlider?.matches?.('product-gallery-slider') &&
          typeof gallerySlider.goToNextSlideFromTap === 'function' &&
          gallerySlider.goToNextSlideFromTap(currentMediaId)
        ) {
          return;
        }

        const currentIndex = mediaItems.findIndex(
          (item) => item.dataset.mediaId === currentMediaId,
        );
        if (currentIndex < 0) return;

        const nextMediaId = mediaItems[(currentIndex + 1) % mediaItems.length]?.dataset?.mediaId;
        if (!nextMediaId) return;

        this.setActiveMedia(nextMediaId, {
          allowViewportScroll: false,
          lockCounterUntilViewerScrollSettled: true,
        });
      }

      onSlideChanged(event) {
        this.activeMediaSync.syncFromSlideChanged(event);
      }

      updateCounter(position) {
        const currentElement = this.elements.viewer.querySelector('.slider-counter--current');
        const totalElement = this.elements.viewer.querySelector('.slider-counter--total');
        if (!currentElement || !totalElement) return;

        const currentPosition = String(position || 1).padStart(2, '0');
        const totalCount = String(this._uniqueTargetCount || 0).padStart(2, '0');
        currentElement.textContent = currentPosition;
        totalElement.textContent = totalCount;
      }

      finishInitialSync() {
        this._isInitialSyncing = false;
      }

      finishInitialViewerScrollSync() {
        this._initialViewerSyncFrame = null;
        this.elements.viewer?.addEventListener('slideChanged', this._onSlideChanged);
        this.finishInitialSync();
      }

      syncInitialViewerPosition(activeMedia) {
        if (!activeMedia || this.mqlDesktop.matches) return false;

        const scrollContainer = activeMedia.parentElement;
        if (!scrollContainer?.scrollTo) return false;

        const nextScrollLeft = activeMedia.offsetLeft;
        const ScrollUtils = getInitialViewerScrollUtils();
        if (!ScrollUtils) return false;
        if (ScrollUtils.isNearScrollLeft(scrollContainer, nextScrollLeft)) return false;

        /* 初回同期の横移動ではslideChanged起点の読み上げや副作用を発火させない */
        this.elements.viewer.removeEventListener('slideChanged', this._onSlideChanged);
        ScrollUtils.scrollLeftWithoutAnimation(scrollContainer, nextScrollLeft);
        this.elements.viewer.resetPages?.();
        this.waitForInitialViewerScrollSettled(scrollContainer);
        return true;
      }

      /*
       * scrollLeft の書き換え直後はブラウザ側のスクロールスナップ確定が非同期のため、
       * scrollend に頼らず2フレーム連続で値が変化しないことを確認してから完了とみなす。
       */
      waitForInitialViewerScrollSettled(scrollContainer, previousLeft = null, stableFrames = 0) {
        window.cancelAnimationFrame(this._initialViewerSyncFrame);
        this._initialViewerSyncFrame = requestAnimationFrame(() => {
          const ScrollUtils = getInitialViewerScrollUtils();
          if (!ScrollUtils) {
            this.finishInitialViewerScrollSync();
            return;
          }
          const currentLeft = scrollContainer.scrollLeft;
          const nextStableFrames = ScrollUtils.getSettledScrollLeftFrameCount({
            currentLeft,
            previousLeft,
            stableFrames,
          });

          if (nextStableFrames >= 2) {
            this.finishInitialViewerScrollSync();
            return;
          }

          this.waitForInitialViewerScrollSettled(scrollContainer, currentLeft, nextStableFrames);
        });
      }

      getThumbnailScrollContainer() {
        if (!this.elements.thumbnails) return null;
        return this.elements.thumbnails.slider || this.elements.thumbnails;
      }

      getThumbnailScrollMetrics(thumbnail, scrollContainer) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const top = thumbnailRect.top - scrollContainerRect.top + scrollContainer.scrollTop;
        const height = thumbnailRect.height;
        return {
          top,
          bottom: top + height,
          center: top + height / 2,
          height,
          maxScrollTop: Math.max(scrollContainer.scrollHeight - scrollContainer.clientHeight, 0),
        };
      }

      setActiveThumbnail(thumbnail, options = {}) {
        if (!this.elements.thumbnails || !thumbnail) return;
        /* setActiveMedia -> syncNavigationState 経由で既に解決済みの options を受け取る */
        const activeMediaOptions = options.thumbnailAlign
          ? options
          : this.getActiveMediaOptions(options);

        for (const element of this.elements.thumbnails.querySelectorAll('button')) {
          element.removeAttribute('aria-current');
        }
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!activeMediaOptions.scrollThumbnail) return;

        const scrollContainer = this.getThumbnailScrollContainer();
        if (!scrollContainer?.scrollTo) return;

        const metrics = this.getThumbnailScrollMetrics(thumbnail, scrollContainer);
        if (activeMediaOptions.thumbnailAlign === 'start' && this.mqlDesktop.matches) {
          const nextScrollTop = Math.min(Math.max(metrics.top, 0), metrics.maxScrollTop);

          if (Math.abs(scrollContainer.scrollTop - nextScrollTop) < 4) return;

          scrollContainer.scrollTo({ top: nextScrollTop, behavior: activeMediaOptions.behavior });
          return;
        }

        const visibleOffset = 10;
        const visibleTop = scrollContainer.scrollTop + visibleOffset;
        const visibleBottom =
          scrollContainer.scrollTop + scrollContainer.clientHeight - visibleOffset;

        if (metrics.top >= visibleTop && metrics.bottom <= visibleBottom) {
          return;
        }

        const nextScrollTop =
          metrics.top < visibleTop
            ? Math.max(metrics.top - visibleOffset, 0)
            : Math.min(
                Math.max(metrics.bottom - scrollContainer.clientHeight + visibleOffset, 0),
                metrics.maxScrollTop,
              );

        scrollContainer.scrollTo({ top: nextScrollTop, behavior: activeMediaOptions.behavior });
      }

      syncNavigationState(mediaId, options = {}) {
        if (!this.navigationItems.length) return;

        for (const item of this.navigationItems) {
          const button = item.querySelector('button');
          if (!button) continue;
          if (item.dataset.target === mediaId) {
            button.setAttribute('aria-current', true);
          } else {
            button.removeAttribute('aria-current');
          }
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail, options);
      }

      /* メイン画像の描画高さにサムネイル列の max-height を同期する */
      syncThumbnailHeight() {
        if (!this.elements.thumbnails || !this.mqlDesktop?.matches) {
          this.style.removeProperty('--thumbnail-max-height');
          return;
        }
        const activeItem = this.elements.viewer?.querySelector('.product__media-item.is-active');
        if (!activeItem) return;
        const height = activeItem.offsetHeight;
        if (height > 0) {
          this.style.setProperty('--thumbnail-max-height', `${height}px`);
        }
      }

      disconnectedCallback() {
        this.elements.viewer?.removeEventListener('slideChanged', this._onSlideChanged);
        this.elements.viewer?.removeEventListener('click', this._onMediaItemClick);
        this.elements.viewer?.removeEventListener(
          'touchstart',
          this._enableSlideChangeAnnouncements,
        );
        this.removeEventListener('click', this._onNavigationClick);
        this._previousButton?.removeEventListener('click', this._onPreviousButtonClick, true);
        this._nextButton?.removeEventListener('click', this._onNextButtonClick, true);
        this._previousButton = null;
        this._nextButton = null;

        if (this._thumbnailResizeObserver) {
          this._thumbnailResizeObserver.disconnect();
          this._thumbnailResizeObserver = null;
        }

        if (this._initialViewerSyncFrame) {
          window.cancelAnimationFrame(this._initialViewerSyncFrame);
          this._initialViewerSyncFrame = null;
        }
        this.activeMediaSync?.disconnect();
        this._isInitialSyncing = false;
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        for (const slide of this.elements.viewer.sliderItems) {
          slide.setAttribute('role', 'presentation');
        }
      }
    },
  );
}
